import type { Asset } from "@cva/shared";
import { useQuery } from "@tanstack/react-query";
import { Check, ImagePlus, Search } from "lucide-react";
import { useState } from "react";
import { orpc } from "../lib/api";
import { cn } from "../lib/utils";
import { FolderNav, useFolderBrowser } from "./media-folders";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter } from "./ui/dialog";

type PickerKind = "image" | "svg" | "video";

const KIND_LABELS: Record<PickerKind, string> = {
  image: "Bilder",
  svg: "Grafiken (SVG)",
  video: "Videos",
};

/** Picks images or SVG graphics (optionally videos) from the media library. */
export function MediaPickerDialog({
  orgId,
  open,
  onOpenChange,
  title,
  description,
  multiple,
  kinds = ["image", "svg"],
  onPick,
}: {
  orgId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  multiple?: boolean;
  /** Kinds offered in the switch (the first one is shown first). */
  kinds?: PickerKind[];
  onPick: (assets: Asset[]) => void;
}) {
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState<PickerKind>(kinds[0] ?? "image");
  const [selected, setSelected] = useState<Asset[]>([]);
  const folders = useFolderBrowser(orgId, search);
  const media = useQuery({
    ...orpc.media.list.queryOptions({
      input: {
        orgId,
        kind,
        folderId: folders.listFolderId,
        search: search || undefined,
        limit: 120,
      },
    }),
    enabled: open,
  });
  const toggle = (asset: Asset) => {
    if (!multiple) {
      onPick([asset]);
      onOpenChange(false);
      return;
    }
    setSelected((current) =>
      current.some((entry) => entry.id === asset.id)
        ? current.filter((entry) => entry.id !== asset.id)
        : [...current, asset],
    );
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setSelected([]);
        onOpenChange(next);
      }}
    >
      <DialogContent title={title} description={description} className="max-w-3xl">
        <div className="mb-3 flex gap-2">
          <label className="flex h-9 flex-1 items-center gap-2 rounded-lg border border-zinc-300 px-3">
            <Search className="size-4 text-zinc-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Mediathek durchsuchen"
              className="w-full bg-transparent text-sm outline-none"
            />
          </label>
          <div className="flex rounded-lg bg-zinc-100 p-0.5">
            {kinds.map((entry) => (
              <button
                key={entry}
                type="button"
                onClick={() => setKind(entry)}
                className={cn(
                  "rounded-md px-3 text-sm text-zinc-600",
                  kind === entry && "bg-white text-zinc-900 shadow-sm",
                )}
              >
                {KIND_LABELS[entry]}
              </button>
            ))}
          </div>
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          <FolderNav browser={folders} kinds={[kind]} grid />
          {media.data && media.data.items.length === 0 && !folders.hasSubfolders ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center text-sm text-zinc-500">
              <ImagePlus className="size-8 text-zinc-300" />
              {folders.folderId && folders.browsing
                ? "In diesem Ordner ist nichts Passendes."
                : "Nichts gefunden. Lade Logos und Grafiken zuerst in der Mediathek hoch."}
            </div>
          ) : null}
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-5">
            {media.data?.items
              .filter((asset) => asset.status === "ready")
              .map((asset) => {
                const active = selected.some((entry) => entry.id === asset.id);
                return (
                  <button
                    key={asset.id}
                    type="button"
                    title={asset.name}
                    onClick={() => toggle(asset)}
                    className={cn(
                      "relative flex aspect-square items-center justify-center overflow-hidden rounded-lg border border-zinc-200 bg-[repeating-conic-gradient(#f4f4f5_0%_25%,#fff_0%_50%)] bg-[length:12px_12px] p-1.5 hover:ring-2 hover:ring-brand-400",
                      active && "ring-2 ring-brand-600",
                    )}
                  >
                    <img
                      src={asset.thumbnailUrl ?? asset.url}
                      alt={asset.name}
                      loading="lazy"
                      className="max-h-full max-w-full object-contain"
                    />
                    {active ? (
                      <span className="absolute top-1 right-1 rounded-full bg-brand-600 p-0.5 text-white">
                        <Check className="size-3" />
                      </span>
                    ) : null}
                  </button>
                );
              })}
          </div>
        </div>
        {multiple ? (
          <DialogFooter>
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button
              disabled={selected.length === 0}
              onClick={() => {
                onPick(selected);
                setSelected([]);
                onOpenChange(false);
              }}
            >
              {selected.length > 0 ? `${selected.length} hinzufügen` : "Hinzufügen"}
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
